import { useMemo, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Info, Wind } from 'lucide-react';

export interface ACTQuestionOption {
  label: string;
  score: number;
}

export interface ACTQuestionDefinition {
  prompt: string;
  options: ACTQuestionOption[];
}

export interface ACTResultPayload {
  answers: number[];
  total: number;
  classification: string;
  finishedAt?: string;
}

interface ACTTestProps {
  initial?: number[];
  onCancel?: () => void;
  onNext: (result: ACTResultPayload) => void;
}

export const ACT_QUESTIONS: ACTQuestionDefinition[] = [
  {
    prompt: 'A asma prejudicou suas atividades no trabalho, na escola ou em casa?',
    options: [
      { label: 'Nenhuma vez', score: 5 },
      { label: 'Poucas vezes', score: 4 },
      { label: 'Algumas vezes', score: 3 },
      { label: 'Maioria das vezes', score: 2 },
      { label: 'Todo o tempo', score: 1 },
    ],
  },
  {
    prompt: 'Como está o controle da sua asma?',
    options: [
      { label: 'Totalmente descontrolada', score: 1 },
      { label: 'Pobremente controlada', score: 2 },
      { label: 'Um pouco controlada', score: 3 },
      { label: 'Bem controlada', score: 4 },
      { label: 'Completamente controlada', score: 5 },
    ],
  },
  {
    prompt: 'Quantas vezes você teve falta de ar?',
    options: [
      { label: 'De jeito nenhum', score: 5 },
      { label: 'Uma ou duas vezes por semana', score: 4 },
      { label: 'Três a seis vezes por semana', score: 3 },
      { label: 'Uma vez ao dia', score: 2 },
      { label: 'Mais que uma vez ao dia', score: 1 },
    ],
  },
  {
    prompt: 'A asma acordou você à noite ou mais cedo que de costume?',
    options: [
      { label: 'De jeito nenhum', score: 5 },
      { label: 'Uma ou duas vezes', score: 4 },
      { label: 'Uma vez por semana', score: 3 },
      { label: 'Duas a três noites por semana', score: 2 },
      { label: 'Quatro ou mais noites por semana', score: 1 },
    ],
  },
  {
    prompt: 'Quantas vezes você usou o remédio por inalação para alívio?',
    options: [
      { label: 'De jeito nenhum', score: 5 },
      { label: 'Uma vez por semana ou menos', score: 4 },
      { label: 'Poucas vezes por semana', score: 3 },
      { label: 'Uma ou duas vezes por dia', score: 2 },
      { label: 'Três ou mais vezes por dia', score: 1 },
    ],
  },
];

export const getACTClassification = (total?: number | null): string => {
  if (typeof total !== 'number') return '—';
  if (total >= 20) return 'Asma controlada';
  if (total >= 16) return 'Asma parcialmente controlada';
  return 'Asma não controlada';
};

export default function ACTTest({ initial, onCancel, onNext }: ACTTestProps) {
  const [answers, setAnswers] = useState<number[]>(initial ?? Array(ACT_QUESTIONS.length).fill(-1));
  const [step, setStep] = useState(0);

  const allAnswered = answers.every((value) => value > 0);
  const total = useMemo(
    () => answers.reduce((sum, value) => sum + (value > 0 ? value : 0), 0),
    [answers],
  );
  const classification = getACTClassification(total);
  const currentScore = answers[step];

  const setAnswer = (questionIndex: number, score: number) => {
    setAnswers((previous) => {
      const next = [...previous];
      next[questionIndex] = score;
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-blue-900">
        <Info className="mt-0.5 h-5 w-5" />
        <div>
          <p className="font-medium">ACT — Asthma Control Test</p>
          <p className="text-sm opacity-90">
            Considere as <strong>últimas quatro semanas</strong>. O escore total varia de 5 a 25, e valores mais altos indicam melhor controle da asma.
          </p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 md:col-span-2">
          <div className="flex items-center gap-2 text-blue-800">
            <Wind className="h-4 w-4" />
            <span className="text-xs font-black uppercase tracking-[0.25em]">Progresso</span>
          </div>
          <div className="mt-2 text-lg font-semibold text-slate-900">Questão {step + 1} de {ACT_QUESTIONS.length}</div>
          <div className="mt-3 h-2 rounded-full bg-white/80">
            <div
              className="h-2 rounded-full bg-blue-600 transition-all"
              style={{ width: `${((step + 1) / ACT_QUESTIONS.length) * 100}%` }}
            />
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Pontuação</div>
          <div className="mt-2 text-2xl font-black text-slate-900">{total}/25</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
          <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Classificação</div>
          <div className="mt-2 font-semibold text-slate-900">{classification}</div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">Pergunta {step + 1}</div>
            <div className="mt-1 text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Controle da asma</div>
          </div>
          <div className="text-sm text-slate-500">Score selecionado: {currentScore > 0 ? currentScore : '—'}</div>
        </div>

        <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-base font-medium leading-relaxed text-slate-900">{ACT_QUESTIONS[step].prompt}</p>
        </div>

        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
          {ACT_QUESTIONS[step].options.map((option) => (
            <label
              key={`${step}-${option.score}-${option.label}`}
              className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm transition ${
                answers[step] === option.score
                  ? 'border-blue-600 bg-blue-600 text-white'
                  : 'bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <input
                type="radio"
                name={`act_v2_${step}`}
                className="hidden"
                checked={answers[step] === option.score}
                onChange={() => setAnswer(step, option.score)}
              />
              <span className="flex h-6 w-6 items-center justify-center rounded-full border border-current text-xs font-bold">
                {option.score}
              </span>
              <span className="flex-1">{option.label}</span>
            </label>
          ))}
        </div>

        <div className="mt-5 flex items-center justify-between">
          <button
            type="button"
            className="inline-flex items-center rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-gray-200 disabled:opacity-50"
            onClick={() => setStep((current) => Math.max(0, current - 1))}
            disabled={step === 0}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Anterior
          </button>

          {step < ACT_QUESTIONS.length - 1 ? (
            <button
              type="button"
              className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              onClick={() => setStep((current) => Math.min(ACT_QUESTIONS.length - 1, current + 1))}
              disabled={answers[step] < 0}
            >
              Próxima
              <ChevronRight className="ml-1 h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              disabled={!allAnswered}
              onClick={() =>
                allAnswered &&
                onNext({
                  answers,
                  total,
                  classification,
                  finishedAt: new Date().toISOString(),
                })
              }
            >
              Concluir ACT
              <Check className="ml-1 h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button type="button" className="text-sm text-gray-600 hover:text-gray-800" onClick={onCancel}>
          Cancelar
        </button>
        <div className="text-right text-sm text-gray-700">
          <div>Total: {total}/25</div>
          <div>{classification}</div>
        </div>
      </div>
    </div>
  );
}
